/**
 * DiffView — renders a structured diff between two page revisions.
 * Fetches the diff from the API and displays additions in green,
 * deletions in red, and unchanged blocks in default styling.
 */

import React, { useEffect, useState } from 'react';
import * as pagesApi from '../../api/pages-api';
import type { DiffResponse } from '../../api/pages-api';

interface DiffViewProps {
  readonly pageId: string;
  readonly fromTimestamp: string;
  readonly toTimestamp: string;
}

export function DiffView({ pageId, fromTimestamp, toTimestamp }: DiffViewProps): React.ReactElement {
  const [diff, setDiff] = useState<DiffResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadDiff(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const response = await pagesApi.diffRevisions(pageId, fromTimestamp, toTimestamp);
        if (!cancelled) {
          setDiff(response);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load diff');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadDiff();
    return () => { cancelled = true; };
  }, [pageId, fromTimestamp, toTimestamp]);

  if (loading) {
    return (
      <div className="p-3 border rounded" role="status" aria-live="polite">
        <p className="text-sm text-muted-foreground">Loading diff...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 border rounded" role="alert">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!diff || diff.blocks.length === 0) {
    return (
      <div className="p-3 border rounded">
        <p className="text-sm text-muted-foreground">No differences found.</p>
      </div>
    );
  }

  return (
    <section aria-label="Revision diff" className="border rounded overflow-hidden">
      <div className="p-2 bg-muted/50 border-b">
        <p className="text-xs text-muted-foreground">
          Comparing revisions
        </p>
      </div>
      <div className="p-3 space-y-1">
        {diff.blocks.map((block, index) => (
          <DiffBlock key={index} type={block.type} content={block.content} />
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// DiffBlock — renders a single block with appropriate coloring
// ---------------------------------------------------------------------------

interface DiffBlockProps {
  readonly type: 'added' | 'removed' | 'unchanged';
  readonly content: unknown;
}

function DiffBlock({ type, content }: DiffBlockProps): React.ReactElement {
  const text = extractText(content);

  const classMap: Record<string, string> = {
    added: 'bg-green-50 border-l-2 border-green-500 text-green-900 px-2 py-1',
    removed: 'bg-red-50 border-l-2 border-red-500 text-red-900 px-2 py-1 line-through',
    unchanged: 'px-2 py-1 text-foreground',
  };

  const prefixMap: Record<string, string> = {
    added: '+ ',
    removed: '- ',
    unchanged: '  ',
  };

  return (
    <div className={`text-sm font-mono rounded ${classMap[type]}`} role="text">
      <span className="select-none text-muted-foreground">{prefixMap[type]}</span>
      {text}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper — extracts plain text from BlockNote content for display
// ---------------------------------------------------------------------------

function extractText(content: unknown): string {
  if (typeof content === 'string') return content;

  if (content && typeof content === 'object') {
    // Handle BlockNote block structure
    if ('type' in content && 'content' in content) {
      const block = content as { type: string; content: unknown };
      if (Array.isArray(block.content)) {
        return block.content
          .map((item: unknown) => {
            if (item && typeof item === 'object' && 'text' in item) {
              return (item as { text: string }).text;
            }
            return '';
          })
          .join('');
      }
    }

    // Handle plain text wrapper
    if ('text' in content) {
      return (content as { text: string }).text;
    }
  }

  // Fallback: stringify the content
  try {
    return JSON.stringify(content);
  } catch {
    return '[Unable to display content]';
  }
}
